import { Package, GraduationCap, Wrench } from 'lucide-react';

const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const num = (v) => (v == null ? '—' : new Intl.NumberFormat('pt-PT').format(v));
const eur = (v) => (v == null || Number.isNaN(v) ? '—' : new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v));

const LineCard = ({ section, plan, editPlan, icon: Icon, monthlyKey, unitLabel, unitPlural, testid, vatRate }) => {
  const line = plan[section] || {};
  const monthly = Array.isArray(line[monthlyKey]) ? line[monthlyKey] : Array(12).fill(null);
  const totalUnits = monthly.reduce((acc, v) => acc + (v ?? 0), 0);
  const priceGross = Number(line.price_gross) || 0;
  const cost = Number(line.cost) || 0;
  const priceNet = priceGross / (1 + (vatRate || 23) / 100);
  const annualRevenueNet = totalUnits * priceNet;
  const annualCogs = totalUnits * cost;
  const contribution = priceNet - cost;
  const marginPct = priceNet > 0 ? (contribution / priceNet) * 100 : null;

  const updateMonthly = (index, raw) => {
    const value = raw === '' ? null : Math.max(0, Math.round(Number(raw) || 0));
    const next = monthly.map((v, i) => (i === index ? value : v));
    editPlan(section, monthlyKey, next);
  };

  const applyAll = (value) => {
    const parsed = value === '' ? null : Math.max(0, Math.round(Number(value) || 0));
    editPlan(section, monthlyKey, Array(12).fill(parsed));
  };

  return (
    <article className={`extra-line-card ${line.enabled === false ? 'is-off' : ''}`} data-testid={`extra-line-${testid}`}>
      <header className="extra-line-header">
        <div className="extra-line-title">
          <Icon size={16} aria-hidden="true" />
          <input
            type="text"
            value={line.label ?? ''}
            onChange={(e) => editPlan(section, 'label', e.target.value.slice(0, 80))}
            maxLength={80}
            data-testid={`extra-line-${testid}-label`}
            aria-label="Nome da linha de receita"
          />
        </div>
        <label className="extra-line-toggle" title={line.enabled === false ? 'Ativar linha' : 'Desativar linha'}>
          <input
            type="checkbox"
            checked={line.enabled !== false}
            onChange={(e) => editPlan(section, 'enabled', e.target.checked)}
            data-testid={`extra-line-${testid}-enabled`}
          />
          <span>{line.enabled === false ? 'Desativada' : 'Ativa'}</span>
        </label>
      </header>

      <div className="extra-line-price-fields">
        <label>
          <span>Preço médio c/IVA (€)</span>
          <input
            type="number"
            min={0}
            step="any"
            value={line.price_gross ?? ''}
            onChange={(e) => editPlan(section, 'price_gross', e.target.value === '' ? 0 : Number(e.target.value))}
            data-testid={`extra-line-${testid}-price`}
          />
          <small>Sem IVA: {eur(priceNet)}</small>
        </label>
        <label>
          <span>Custo direto (€)</span>
          <input
            type="number"
            min={0}
            step="any"
            value={line.cost ?? ''}
            onChange={(e) => editPlan(section, 'cost', e.target.value === '' ? 0 : Number(e.target.value))}
            data-testid={`extra-line-${testid}-cost`}
          />
          <small>Margem: {marginPct != null ? `${marginPct.toFixed(1)}%` : '—'}</small>
        </label>
      </div>

      <div className="extra-line-monthly">
        <div className="extra-line-monthly-heading">
          <span>{unitPlural} por mês (ano 1)</span>
          <button
            type="button"
            className="extra-line-fill-btn"
            onClick={() => {
              const value = window.prompt(`Preencher todos os 12 meses com o mesmo valor (${unitPlural.toLowerCase()}):`, '0');
              if (value !== null) applyAll(value);
            }}
            data-testid={`extra-line-${testid}-fill`}
          >
            Preencher todos
          </button>
        </div>
        <div className="extra-line-cells">
          {MONTHS.map((label, i) => (
            <label key={label} className="extra-line-cell">
              <span>{label}</span>
              <input
                type="number"
                min={0}
                step={1}
                value={monthly[i] ?? ''}
                placeholder="—"
                onChange={(e) => updateMonthly(i, e.target.value)}
                data-testid={`extra-line-${testid}-month-${i + 1}`}
                aria-label={`${label} · ${unitLabel.toLowerCase()}`}
              />
            </label>
          ))}
        </div>
      </div>

      <footer className="extra-line-summary" data-testid={`extra-line-${testid}-summary`}>
        <div><span>Total ano 1</span><strong>{num(totalUnits)} {unitPlural.toLowerCase()}</strong></div>
        <div><span>Receita s/IVA</span><strong>{eur(annualRevenueNet)}</strong></div>
        <div><span>Custo total</span><strong>{eur(annualCogs)}</strong></div>
        <div><span>Contribuição</span><strong>{eur(annualRevenueNet - annualCogs)}</strong></div>
      </footer>
    </article>
  );
};

export const ExtraLinesEditor = ({ plan, editPlan }) => {
  if (!plan) return null;
  return (
    <section className="plan-group extra-lines-group" data-testid="extra-lines-group">
      <div className="plan-group-heading">
        <h3>Consumíveis, formação e manutenção</h3>
        <p>
          Novas linhas de receita incluídas nas projeções. Cada uma tem preço médio, custo direto e previsão mensal (ano 1).
          Os anos seguintes crescem ao mesmo ritmo dos equipamentos.
        </p>
      </div>
      <div className="extra-lines-grid">
        <LineCard
          section="accessories"
          plan={plan}
          editPlan={editPlan}
          icon={Package}
          monthlyKey="monthly_units"
          unitLabel="Unidade"
          unitPlural="unidades"
          testid="accessories"
          vatRate={plan.vat}
        />
        <LineCard
          section="training"
          plan={plan}
          editPlan={editPlan}
          icon={GraduationCap}
          monthlyKey="monthly_sessions"
          unitLabel="Formação"
          unitPlural="formações"
          testid="training"
          vatRate={plan.vat}
        />
        <LineCard
          section="maintenance"
          plan={plan}
          editPlan={editPlan}
          icon={Wrench}
          monthlyKey="monthly_interventions"
          unitLabel="Intervenção"
          unitPlural="intervenções"
          testid="maintenance"
          vatRate={plan.vat}
        />
      </div>
    </section>
  );
};

const EXTRA_LINE_CONFIGS = {
  accessories: { icon: Package, monthlyKey: 'monthly_units', unitLabel: 'Unidade', unitPlural: 'unidades', testid: 'accessories' },
  training: { icon: GraduationCap, monthlyKey: 'monthly_sessions', unitLabel: 'Formação', unitPlural: 'formações', testid: 'training' },
  maintenance: { icon: Wrench, monthlyKey: 'monthly_interventions', unitLabel: 'Intervenção', unitPlural: 'intervenções', testid: 'maintenance' },
};

export const ExtraLine = ({ section, plan, editPlan }) => {
  const cfg = EXTRA_LINE_CONFIGS[section];
  if (!cfg || !plan) return null;
  return (
    <LineCard
      section={section}
      plan={plan}
      editPlan={editPlan}
      icon={cfg.icon}
      monthlyKey={cfg.monthlyKey}
      unitLabel={cfg.unitLabel}
      unitPlural={cfg.unitPlural}
      testid={cfg.testid}
      vatRate={plan.vat}
    />
  );
};
